import dayjs from 'dayjs';

/**
 * A versioned rate schedule (BAR-3039): the Receivables fee schedule now, the
 * Payables cost schedule in Phase 3. Rates arrive per partner with one start
 * date, so the schedule is grouped by partner and the partner is acted on.
 */

/** The cost schedule's variable fee (BAR-3047); `baseCoversMinutes` null is Unlimited. */
export interface VariableFee {
  baseCoversMinutes: number | null;
  increment: number | null;
  incrementEveryMinutes: number | null;
}

/** One version of a rate; dates are `YYYY-MM-DD`, inclusive. */
export interface RateVersion {
  id: string;
  procedure: string;
  rate: number;
  /** Only on the cost schedule's partner rates. */
  variable?: VariableFee;
  effectiveFrom: string;
  effectiveUntil: string | null;
  /** `Practitioner/<id>`, or `seed`. */
  setBy: string;
  correctedAt: string | null;
  correctedBy: string | null;
  correctionReason: string | null;
  previousRate: number | null;
}

export interface RateGroup {
  key: string;
  name: string;
  versions: RateVersion[];
}

/** One procedure's history: the version in force, one scheduled after it, and the earlier ones. */
export interface RateSeries {
  procedure: string;
  /** In force today; null when the only version is still to come. */
  current: RateVersion | null;
  scheduled: RateVersion | null;
  earlier: RateVersion[];
}

export function rateSeries(versions: RateVersion[], today: string): RateSeries[] {
  const byProcedure = new Map<string, RateVersion[]>();
  for (const version of versions) {
    byProcedure.set(version.procedure, [...(byProcedure.get(version.procedure) ?? []), version]);
  }
  return [...byProcedure.entries()].map(([procedure, list]) => {
    const sorted = [...list].sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom));
    const current = [...sorted].reverse().find((version) => version.effectiveFrom <= today) ?? null;
    const latest = sorted[sorted.length - 1];
    const scheduled = latest.effectiveFrom > today ? latest : null;
    const shown = current ?? scheduled;
    return {
      procedure,
      current,
      scheduled,
      earlier: sorted.filter((version) => shown && version.effectiveFrom < shown.effectiveFrom).reverse(),
    };
  });
}

/** The group's scheduled update date, for its `Update from {date}` badge. */
export function scheduledFrom(series: RateSeries[]): string | null {
  return series.find((entry) => entry.scheduled)?.scheduled?.effectiveFrom ?? null;
}

/** `Oct 1, 2026`. */
export const formatRateDate = (date: string): string => dayjs(date).format('MMM D, YYYY');

/** `Oct 1`. */
export const formatRateShortDate = (date: string): string => dayjs(date).format('MMM D');

/** A drawer row: an existing procedure, or one being added. */
export interface RateDraftRow {
  procedure: string;
  /** As typed. */
  rate: string;
  /** False for a procedure added in this drawer. */
  existing: boolean;
  /** The variable fee as chosen and typed (BAR-3047): base covers `''` is Unlimited. */
  baseCovers?: string;
  increment?: string;
  every?: string;
}

/** The rate typed, in dollars; null when empty or not a number. */
export function parseRate(value: string): number | null {
  if (!value.trim()) return null;
  const rate = Number.parseFloat(value.replace(/[^0-9.]/g, ''));
  return Number.isFinite(rate) && rate > 0 ? Math.round(rate * 100) / 100 : null;
}

/** The row's variable fee, or undefined on a schedule without one. */
export function draftVariable(row: RateDraftRow): VariableFee | undefined {
  if (row.baseCovers === undefined) return undefined;
  const covers = row.baseCovers ? Number(row.baseCovers) : null;
  return {
    baseCoversMinutes: covers,
    increment: covers === null ? null : parseRate(row.increment ?? ''),
    incrementEveryMinutes: covers === null || !row.every ? null : Number(row.every),
  };
}

/** A timed base needs an increment and its interval; Unlimited needs neither. */
export function variableComplete(row: RateDraftRow): boolean {
  const variable = draftVariable(row);
  return (
    !variable ||
    variable.baseCoversMinutes === null ||
    (variable.increment !== null && variable.incrementEveryMinutes !== null)
  );
}

/** The row as typed against a version: same rate, and same variable fee where there is one. */
function sameAs(row: RateDraftRow, rate: number | null, version: RateVersion | null | undefined): boolean {
  if (!version || rate !== version.rate) return false;
  const variable = draftVariable(row);
  if (!variable) return true;
  const was = version.variable ?? { baseCoversMinutes: null, increment: null, incrementEveryMinutes: null };
  return (
    variable.baseCoversMinutes === was.baseCoversMinutes &&
    variable.increment === was.increment &&
    variable.incrementEveryMinutes === was.incrementEveryMinutes
  );
}

/**
 * What Update rates would change: a new procedure, a changed rate, or a new
 * date for the scheduled update. `rateOf` reads an optional row's blank as $0.
 */
export function updateChanges(
  rows: RateDraftRow[],
  series: RateSeries[],
  effectiveFrom: string,
  pendingFrom: string | null,
  rateOf: (row: RateDraftRow) => number | null = (row) => parseRate(row.rate)
): boolean {
  return rows.some((row) => {
    const entry = series.find((candidate) => candidate.procedure === row.procedure);
    const rate = rateOf(row);
    // A blank optional row (no daily minimum) where there was none changes nothing.
    if (!entry && rate === 0) return false;
    if (!row.existing) return true;
    const shown = entry?.scheduled ?? entry?.current;
    const now = entry?.current;
    if (entry?.scheduled && now && sameAs(row, rate, now)) return true;
    return !sameAs(row, rate, shown) || (pendingFrom !== null && effectiveFrom !== pendingFrom);
  });
}

/** What Correct would change: a rate in force typed differently. */
export function correctChanges(
  rows: RateDraftRow[],
  series: RateSeries[],
  rateOf: (row: RateDraftRow) => number | null = (row) => parseRate(row.rate)
): boolean {
  return rows.some((row) => {
    const now = series.find((candidate) => candidate.procedure === row.procedure)?.current;
    return now !== undefined && now !== null && !sameAs(row, rateOf(row), now);
  });
}
